import { dates, MenuItem } from "@omegagrid/core";
import { FormContainer } from "@omegagrid/form";
import { NodeModel, TreeOptions, TreeSource } from "@omegagrid/tree";
import { BrowserAdapter } from "@reporting/rp-app-host";
import { ChatMessage } from "@reporting/rp-types/ai";
import rp from "@reporting/rp-app-host";
import { getLocale, msg } from "@omegagrid/localize";
import { css, html, render } from "lit";
import { EntityAccessType } from "@reporting/rp-types/users";
import { ChatRenameFormAdapter } from "./chatRenameFormAdapter";

export class ChatBrowserAdapter extends BrowserAdapter {

	constructor() { super() }

	_dateFormatter: dates.DateFormatterFunction = null;
	get dateFormatter() {
		if (!this._dateFormatter) {
			this._dateFormatter = dates.getDateFormatter(getLocale(), dates.getDateFormat({withTime: false}));
		}
		return this._dateFormatter;
	}

	async getTree(): Promise<TreeSource> {
		const items = await rp.ms('ai').get<ChatMessage[]>(`chats`, {
			fields: ['id_chat', 'name', 'date_created', 'shared'],
			order: [['shared', 'desc'], ['date_created', 'desc']],
		});

		const treeItems: TreeSource = [];
		let lastDate: string = null;
		let sharedAdded = false;
		items.forEach(item => {
			if (item.shared) {
				if (!sharedAdded) {
					treeItems.push({
						k: 'shared',
						v: msg('Shared'),
						collapsed: true,
						l: 0,
						folder: {
							openIcon: {icon: ['fal', 'folder-open'], statusIcon: ['fas', 'users']},
							closedIcon: {icon: ['fal', 'folder'], statusIcon: ['fas', 'users']},
						},
					});
					sharedAdded = true;
				}
			} else {
				const date = item.date_created?.substring(0, 10);
				const collapsed = this.dateFormatter(item.date_created) !== this.dateFormatter(new Date());

				if (date != lastDate) {
					treeItems.push({
						k: `date-${date}`,
						v: this.dateFormatter(item.date_created),
						l: 0,
						collapsed,
					});
				}

				lastDate = date;
			}

			treeItems.push({
				k: item.id_chat.toString() + (item.shared ? '-readonly' : ''),
				v: item.name,
				l: 1,
				props: {chat: item}
			});
		});

		return treeItems;
	}

	_nodeRenderer = (div: HTMLDivElement, node: NodeModel) => {
		if (!node.properties) {
			div.textContent = node.value as string;
			return;
		}

		const chat = node.properties.chat as ChatMessage;
		const time = dates.format(chat.date_created, 'HH:mm');

		render(html`
			<div class="node-content">
				<og-icon icon="comments"></og-icon>
				<span>${time}</span>
				<span>&nbsp|&nbsp</span>
				<span class="name">
					${chat.name}
				</span>
			</div>
		`, div);
	}

	async getMenuItems(node: NodeModel): Promise<MenuItem[]> {
		const chat = node.properties?.chat as ChatMessage;
		if (!chat || chat.shared) return [];
		return [
			{ key: 'rename', value: msg('Rename'), icon: 'pen-to-square' },
			{ key: 'shareUsers', value: `${msg('Sharing')} - ${msg('Users')}` , icon: 'users' },
			{ key: 'delete', value: msg('Delete'), icon: 'trash', color: '#ff0000' }
		];
	}

	async onAction(key: string, node: NodeModel) {
		const chat = node.properties?.chat as ChatMessage;
		switch (key) {
			case 'rename':
				if (!chat) return;
				const form = await rp.host.createComponent('ai-renameForm') as unknown as FormContainer;
				const adapter = form.adapter as ChatRenameFormAdapter;
				adapter.idChat = chat.id_chat;
				adapter.name = chat.name;
				form.addEventListener('form.save', () => this.browser?.refresh());
				rp.host.openModalSidebar({ component: form, caption: msg('Rename') });
				break;
			case 'shareUsers':
				rp.host.open(`users-entityUsers-${EntityAccessType.Chat}-${chat.id_chat}`, { title: `${msg('Share')}: Chat #${chat.id_chat}` });
				break;
			case 'delete':
				if (!chat) return;
				const command = await rp.host.openMenu({
					content: 'yes_no',
					text: `${msg('Are you sure you want to delete')} #${chat.id_chat}?`
				});
				if (command?.id == 'Y') {
					await rp.ms('ai').delete(`chats/${chat.id_chat}`);
					this.browser.deleteNode(node);
				}
				break;
		}
	}

	getOptions(): Partial<TreeOptions> {
		return this.createOptions({
			nodeRenderer: this._nodeRenderer,
			contextMenuButton: true,
			selectMode: 'none',
			expanders: false,
			search: true,
			customStyle: css`
				.node-content {
					display: flex;
					flex-direction: row;
					align-items: center;
				}
			`,
			folderOptions: {
				openIcon: {icon: ['fal', 'folder-open'], statusIcon: ['fas', 'calendar']},
				closedIcon: {icon: ['fal', 'folder'], statusIcon: ['fas', 'calendar']},
			}
		});
	}
}
